<template>
  <div
    id="pageDashboard"
    v-if="hasPermission('STATS') || hasPermission('ADMIN_STATS')"
  >
    <v-tabs centered color="cyan" dark icons-and-text>
      <v-tabs-slider color="yellow"></v-tabs-slider>

      <v-tab href="#basics" v-if="hasPermission('STATS')">
        {{ $t("monitoring.dashboard.basic") }}
        <v-icon> fa fa-eye </v-icon>
      </v-tab>

      <v-tab
        href="#weekly_financial"
        v-if="hasPermission('WEEKLY_FINANCIAL_STATS')"
      >
        {{ $t("monitoring.dashboard.financial") }}
        <v-icon> attach_money </v-icon>
      </v-tab>

      <v-tab href="#passengers" v-if="hasPermission('PASSENGERS_STATS')">
        {{ $t("monitoring.dashboard.passengers") }}
        <v-icon> fa fa-user-o </v-icon>
      </v-tab>

      <v-tab href="#captains" v-if="hasPermission('CAPTAIN_STATS')">
        {{ $t("monitoring.dashboard.captains") }}
        <v-icon> fa fa-user-secret </v-icon>
      </v-tab>

      <v-tab href="#mixed" v-if="hasPermission('MIXED_STATS')">
        {{ $t("monitoring.dashboard.mixed") }}
        <v-icon> fa fa-random </v-icon>
      </v-tab>

      <v-tab href="#osm" v-if="hasPermission('OSM_STATS')">
        OSM
        <v-icon> map </v-icon>
      </v-tab>

      <v-tab-item value="basics" lazy>
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.signupsToday')">
                <div slot="widget-content" class="headline text-xs-center">
                  {{ stats.signUpsToday }}
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.approvedPassengersToday')">
                <div slot="widget-content" class="headline text-xs-center">
                  {{ stats.approvedPassengersToday }}
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.unapprovedPassengersEver')">
                <div slot="widget-content" class="headline text-xs-center">
                  {{ stats.unApprovedPassengersEver }}
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.unapprovedPassengersToday')">
                <div slot="widget-content">
                  <unapproved-passengers-pie-chart
                    v-if="
                      stats.unApprovedPassengersWithIdsToday > 0 ||
                      stats.unApprovedPassengersWithoutIdsToday
                    "
                    :un-approved-passengers-with-ids-today="
                      stats.unApprovedPassengersWithIdsToday
                    "
                    :un-approved-passengers-without-ids-today="
                      stats.unApprovedPassengersWithoutIdsToday
                    "
                  />
                  <div v-else class="headline text-xs-center">0</div>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.bookingsCountYesterday')">
                <div slot="widget-content" class="headline text-xs-center">
                  <non-pooling-vs-pooling-bookings-pie-chart
                    v-if="
                      stats.bookingsYesterdayPerService &&
                      stats.bookingsYesterdayPerService.reduce(
                        (sum, r) => sum + r.value,
                        0
                      ) > 0
                    "
                    :value="stats.bookingsYesterdayPerService"
                  />
                  <div v-else class="headline text-xs-center">0</div>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.incompleteBookingsYesterday')">
                <div slot="widget-content" class="headline text-xs-center">
                  <rejected-vs-cancelled-bookings-pie-chart
                    v-if="
                      stats.rejectedBookingsYesterday > 0 ||
                      stats.cancelledBookingsYesterday > 0
                    "
                    :rejected-bookings="stats.rejectedBookingsYesterday"
                    :cancelled-bookings="stats.cancelledBookingsYesterday"
                    :all-bookings-count="stats.allBookingsYesterday"
                  />
                  <div v-else class="headline text-xs-center">0</div>
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 sm12 xs12>
              <v-widget :title="$t('monitoring.dashboard.averageRatingToday')">
                <div slot="widget-content" class="headline text-xs-center">
                  <template v-if="stats.averageRatingToday !== null">
                    {{ stats.averageRatingToday }}
                  </template>
                  <template v-else> {{ $t("monitoring.dashboard.noRatingsToday") }} </template>
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item
        v-if="hasPermission('WEEKLY_FINANCIAL_STATS')"
        value="weekly_financial"
        lazy
      >
        <v-container grid-list-xl fluid>
          <loading-component :value="loading"></loading-component>
          <choose-date v-model="date"></choose-date>
          <h1 style="text-align: center">{{ $t("monitoring.dashboard.weeklyFinancial") }}</h1>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs6
              ><h2 style="text-align: center">
                {{ $t("monitoring.dashboard.fromValue", { value: financial_stats.period[0] }) }}
              </h2></v-flex
            >
            <v-flex xs6
              ><h2 style="text-align: center">
                {{ $t("monitoring.dashboard.toValue", { value: financial_stats.period[1] }) }}
              </h2></v-flex
            >
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.revenueValue", { value: financial_stats.revenue }) }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.feeValue", { value: financial_stats.fee }) }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.payrollValue", { value: financial_stats.payroll }) }}
              </h3></v-flex
            >
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.discountValue", { value: financial_stats.discount }) }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.refundValue", { value: financial_stats.refund }) }}
              </h3></v-flex
            >
            <v-flex xs4
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.penaltiesValue", { value: financial_stats.penalties }) }}
              </h3>
            </v-flex>
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs6
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.bonusCaptainValue", { value: financial_stats.bonus_captain }) }}
              </h3>
            </v-flex>
            <v-flex xs6
              ><h3 style="text-align: center">
                {{ $t("monitoring.dashboard.bonusPassengerValue", { value: financial_stats.bonus_passenger }) }}
              </h3></v-flex
            >
          </v-layout>
          <v-layout style="margin: 15px" row wrap>
            <v-flex xs12
              ><h1 style="text-align: center">
                {{ $t("monitoring.dashboard.profitValue", { value: financial_stats.profit }) }}
              </h1></v-flex
            >
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item
        v-if="hasPermission('PASSENGERS_STATS')"
        value="passengers"
        lazy
      >
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex xs12>
              <v-widget :title="$t('monitoring.dashboard.customizations')">
                <v-layout slot="widget-content">
                  <v-flex xs12 md4>
                    <v-select
                      :items="groupByItems"
                      :label="$t('monitoring.dashboard.groupBy')"
                      v-model="groupBy"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="startDate"
                      :label="$t('monitoring.dashboard.startDate')"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate clearable v-model="endDate" :label="$t('monitoring.dashboard.endDate')" />
                  </v-flex>
                </v-layout>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.bookings')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.bookingsCountPerDay]"
                    :background-colors="['#0cb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF']"
                    :labels="[$t('monitoring.dashboard.bookingsCount')]"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.successfulBookings')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.successfulBookingsCountPerDay]"
                    :background-colors="['#0cb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.bookingsCount')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.rejectedBookings')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.rejectedBookingsCountPerDay]"
                    :background-colors="['#0cb']"
                    :border-colors="['#FFF']"
                    :default-group-by="groupBy"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.bookingsCount')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.acquiredVsApprovedPassengers')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.acquiredPassengersPerDay,
                      stats.approvedPassengersPerDay,
                    ]"
                    :background-colors="['#0cb', '#0fb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF', '#FFF']"
                    stacked
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.acquiredPassengers'), $t('monitoring.dashboard.approvedPassengers')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.activePassengers')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.activePassengersPerDay]"
                    :background-colors="['#0cb']"
                    :can-group-by="false"
                    :border-colors="['#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.activePassengers')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.passengersMonthBookingDatetime')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerMonthDatetime,
                      stats.returningPassengersPerMonthDatetime,
                      stats.newPassengersPerMonthDatetime,
                      stats.lostPassengersPerMonthDatetime,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.passengersWeekBookingDatetime')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerWeekDatetime,
                      stats.returningPassengersPerWeekDatetime,
                      stats.newPassengersPerWeekDatetime,
                      stats.lostPassengersPerWeekDatetime,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.passengersMonthBookingCreation')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerMonthCreation,
                      stats.returningPassengersPerMonthCreation,
                      stats.newPassengersPerMonthCreation,
                      stats.lostPassengersPerMonthCreation,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.passengersWeekBookingCreation')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constPassengersPerWeekCreation,
                      stats.returningPassengersPerWeekCreation,
                      stats.newPassengersPerWeekCreation,
                      stats.lostPassengersPerWeekCreation,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item v-if="hasPermission('CAPTAIN_STATS')" value="captains" lazy>
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex xs12>
              <v-widget :title="$t('monitoring.dashboard.customizations')">
                <v-layout slot="widget-content">
                  <v-flex xs12 md4>
                    <v-select
                      :items="groupByItems"
                      :label="$t('monitoring.dashboard.groupBy')"
                      v-model="groupBy"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="startDate"
                      :label="$t('monitoring.dashboard.startDate')"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate clearable v-model="endDate" :label="$t('monitoring.dashboard.endDate')" />
                  </v-flex>
                </v-layout>
              </v-widget>
            </v-flex>

            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.captainsFirstRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.captainsWhoMadeTheirFirstBookingPerDay]"
                    :background-colors="['#0cb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.captainsCount')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.ghostVsMeterRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.avgGhostRidesPerDay,
                      stats.avgMeterRidesPerDay,
                    ]"
                    :background-colors="['#0cb', '#0fb']"
                    :border-colors="['#FFF', '#FFF']"
                    :default-group-by="groupBy"
                    group-function="avg"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.avgGhostRide'), $t('monitoring.dashboard.avgMeterRide')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.acquiredVsApprovedCaptains')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.acquiredCaptainsPerDay,
                      stats.approvedCaptainsPerDay,
                    ]"
                    :background-colors="['#0cb', '#0fb']"
                    :default-group-by="groupBy"
                    :border-colors="['#FFF', '#FFF']"
                    stacked
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.acquiredCaptains'), $t('monitoring.dashboard.approvedCaptains')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.activeCaptains')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.activeCaptainsPerDay]"
                    :background-colors="['#0cb']"
                    :border-colors="['#FFF']"
                    :can-group-by="false"
                    stacked
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.activeCaptains')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.onlineCaptains')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[stats.onlineCaptainsPerDay]"
                    :background-colors="['#0cb']"
                    :border-colors="['#FFF']"
                    :can-group-by="false"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.onlineCaptains')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.captainsWeekChecklog')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerWeekCheckLog,
                      stats.returningCaptainsPerWeekCheckLog,
                      stats.newCaptainsPerWeekCheckLog,
                      stats.lostCaptainsPerWeekCheckLog,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.captainsMonthChecklog')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerMonthCheckLog,
                      stats.returningCaptainsPerMonthCheckLog,
                      stats.newCaptainsPerMonthCheckLog,
                      stats.lostCaptainsPerMonthCheckLog,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.captainsWeekRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerWeekRide,
                      stats.returningCaptainsPerWeekRide,
                      stats.newCaptainsPerWeekRide,
                      stats.lostCaptainsPerWeekRide,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
            <v-flex lg4 md6 sm12>
              <v-widget :title="$t('monitoring.dashboard.captainsMonthRide')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.constCaptainsPerMonthRide,
                      stats.returningCaptainsPerMonthRide,
                      stats.newCaptainsPerMonthRide,
                      stats.lostCaptainsPerMonthRide,
                    ]"
                    :background-colors="['#0FF', '#0F8', '#00F', '#F00']"
                    :can-group-by="false"
                    :border-colors="['#FFF', '#FFF', '#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    stacked
                    :labels="[$t('monitoring.dashboard.const'), $t('monitoring.dashboard.returning'), $t('monitoring.dashboard.new'), $t('monitoring.dashboard.lost')]"
                  />
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item v-if="hasPermission('MIXED_STATS')" value="mixed" lazy>
        <v-container grid-list-xl fluid>
          <v-layout row wrap>
            <v-flex xs12>
              <v-widget :title="$t('monitoring.dashboard.customizations')">
                <v-layout slot="widget-content">
                  <v-flex xs12 md4>
                    <v-select
                      :items="groupByItems"
                      :label="$t('monitoring.dashboard.groupBy')"
                      v-model="groupBy"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate
                      clearable
                      v-model="startDate"
                      :label="$t('monitoring.dashboard.startDate')"
                    />
                  </v-flex>
                  <v-flex xs12 md4>
                    <ChooseDate clearable v-model="endDate" :label="$t('monitoring.dashboard.endDate')" />
                  </v-flex>
                </v-layout>
              </v-widget>
            </v-flex>

            <v-flex lg3 md4 sm12>
              <v-widget :title="$t('monitoring.dashboard.avgRating')">
                <div slot="widget-content">
                  <BarWithGrouping
                    :data="[
                      stats.avgCaptainRatingPerDay,
                      stats.avgPassengerRatingPerDay,
                    ]"
                    :background-colors="['#0cb', '#0FF']"
                    :default-group-by="groupBy"
                    group-function="avg"
                    :border-colors="['#FFF', '#FFF']"
                    :initial-start-date="startDate"
                    :initial-end-date="endDate"
                    :labels="[$t('monitoring.dashboard.captainRate'), $t('monitoring.dashboard.passengerRate')]"
                  />
                </div>
              </v-widget>
            </v-flex>
          </v-layout>
        </v-container>
      </v-tab-item>

      <v-tab-item v-if="hasPermission('OSM_STATS')" value="osm" lazy>
        <v-container grid-list-xl fluid>
          <osm-stats></osm-stats>
        </v-container>
      </v-tab-item>
    </v-tabs>
  </div>
</template>

<script>
import VWidget from "@/components/VWidget";
import Material from "vuetify/es5/util/colors";
import moment from "moment";
import UnapprovedPassengersPieChart from "@/components/widgets/statistic/UnapprovedPassengersPieChart";
import NonPoolingVsPoolingBookingsPieChart from "@/components/widgets/statistic/NonPoolingVsPoolingBookingsPieChart";
import RejectedVsCancelledBookingsPieChart from "@/components/widgets/statistic/RejetedVsCancelledBookingsPieChart";
import BarWithGrouping from "@/components/widgets/statistic/BarWithGrouping";
import ChooseDate from "@/components/monaco/input/ChooseDate";
import LoadingComponent from "@/components/LoadingComponent";
import DataTable from "@/components/monaco/DataTable";
import OsmStats from "@/components/monaco/OsmStats";

export default {
  title() {
    return this.$t("monitoring.dashboard.title");
  },
  components: {
    OsmStats,
    DataTable,
    LoadingComponent,
    VWidget,
    UnapprovedPassengersPieChart,
    NonPoolingVsPoolingBookingsPieChart,
    RejectedVsCancelledBookingsPieChart,
    BarWithGrouping,
    ChooseDate,
  },
  data() {
    return {
      groupBy: "month",
      groupByOptions: ["day", "week", "month", "quarter", "year"],
      startDate: null,
      endDate: null,
      color: Material,
      date: "",
      loading: false,
      stats: {
        unApprovedPassengersEver: 0,
        unApprovedPassengersWithIdsToday: 0,
        unApprovedPassengersWithoutIdsToday: 0,
        approvedPassengersToday: 0,
        signUpsToday: 0,
        allBookingsYesterday: 0,
        cancelledBookingsYesterday: 0,
        poolingBookingsYesterday: 0,
        rejectedBookingsYesterday: 0,
        averageRatingToday: 0,
        activeCaptainsPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        captainsWhoMadeTheirFirstBookingPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        acquiredCaptainsPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        approvedCaptainsPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        acquiredPassengersPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        approvedPassengersPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        activePassengersPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        avgGhostRidesPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        avgMeterRidesPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        avgPassengerRatingPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        avgCaptainRatingPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        onlineCaptainsPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        bookingsCountPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        successfulBookingsCountPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        rejectedBookingsCountPerDay: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningPassengersPerWeekDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newPassengersPerWeekDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constPassengersPerWeekDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostPassengersPerWeekDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningPassengersPerMonthDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newPassengersPerMonthDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constPassengersPerMonthDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostPassengersPerMonthDatetime: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningPassengersPerWeekCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newPassengersPerWeekCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constPassengersPerWeekCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostPassengersPerWeekCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningPassengersPerMonthCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newPassengersPerMonthCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constPassengersPerMonthCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostPassengersPerMonthCreation: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningCaptainsPerWeekCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newCaptainsPerWeekCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constCaptainsPerWeekCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostCaptainsPerWeekCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningCaptainsPerMonthCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newCaptainsPerMonthCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constCaptainsPerMonthCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostCaptainsPerMonthCheckLog: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningCaptainsPerWeekRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newCaptainsPerWeekRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constCaptainsPerWeekRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostCaptainsPerWeekRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        returningCaptainsPerMonthRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        newCaptainsPerMonthRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        constCaptainsPerMonthRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        lostCaptainsPerMonthRide: [
          { value: 0, date: moment().format("YYYY-MM-DD") },
        ],
        bookingsYesterdayPerService: [
          { service_id: 1, name: "Pooling", value: 12 },
        ],
      },
      financial_stats: {
        period: [
          this.moment().startOf("week").format("YYYY-MM-DD HH:mm:ss"),
          this.moment().endOf("week").format("YYYY-MM-DD HH:mm:ss"),
        ],
      },
    };
  },
  computed: {
    groupByItems() {
      return this.groupByOptions.map((value) => ({
        text: this.$t(`monitoring.dashboard.groupByOptions.${value}`),
        value,
      }));
    },
  },
  methods: {
    getFinancialStats() {
      this.loading = true;
      this.axios.get(this.api.ACTIONS.STATISTICS).then(({ data: { data } }) => {
        this.stats = data;
      });
      this.axios
        .get(
          "statistics/weekly_financial" +
            (this.date ? `?date=${this.date}` : "")
        )
        .then((res) => {
          this.financial_stats = Object.assign(
            this.financial_stats,
            res.data.data
          );
          this.financial_stats.profit =
            parseInt(this.financial_stats.revenue) +
            parseInt(this.financial_stats.bonus_passenger) +
            parseInt(this.financial_stats.fee) +
            parseInt(this.financial_stats.payroll) +
            parseInt(this.financial_stats.discount) +
            parseInt(this.financial_stats.refund) +
            parseInt(this.financial_stats.bonus_captain) +
            parseInt(this.financial_stats.penalties);
        })
        .catch((err) => console.log(err.message))
        .finally(() => (this.loading = false));
    },
  },
  watch: {
    date() {
      this.getFinancialStats();
    },
  },
  mounted() {
    this.getFinancialStats();
  },
};
</script>
